"use client";
import React, { useState } from "react";
import { MdHelp, MdSpaceDashboard } from "react-icons/md";
import { BsFillCollectionFill } from "react-icons/bs";
import { LuDatabase } from "react-icons/lu";
import { executeMigration } from "~/lib/network/metabase-connection";
import { FiCheckCircle } from "react-icons/fi";

type MigrationProps = {
  previewData: any[];
  jobId: string | null;
  handleCancel: () => void;
};

const getIcon = (type: string) => {
  if (!type) return <MdHelp className="w-6 h-6 text-info opacity-90" />;

  switch (type.toLowerCase()) {
    case "database":
      return <LuDatabase className="w-6 h-6 text-primary opacity-90" />;
    case "question":
      return <MdHelp className="w-6 h-6 text-info opacity-90" />;
    case "dashboard":
      return <MdSpaceDashboard className="w-6 h-6 text-lime-400 opacity-90" />;
    case "collection":
      return (
        <BsFillCollectionFill className="w-6 h-6 text-purple-400 opacity-90" />
      );
    default:
      return <MdHelp className="w-6 h-6 text-info opacity-90" />;
  }
};

const Migration: React.FC<MigrationProps> = ({
  previewData,
  jobId,
  handleCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [migrationSummary, setMigrationSummary] = useState<any>(null);

  const handleExecuteMigration = async () => {
    if (!jobId) {
      setError("No job ID available");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await executeMigration({
        jobId,
        selectedItems: previewData,
      });

      setMigrationComplete(true);
      setMigrationSummary(response.migration_results || response);
    } catch (err: any) {
      setError(err.message || "Failed to execute migration");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full overflow-y-auto overflow-hidden">
      <h2 className="text-xl font-bold mb-4">3. Review & Execute Migration</h2>

      <>
        <p className="mb-4">Review the items that will be migrated to Gamgee</p>

        {/* Preview List */}
        {previewData && previewData.length > 0 && (
          <ul className="list bg-base-100 rounded-box shadow-md mt-6 max-h-96 overflow-y-auto">
            <li className="p-4 pb-2 text-sm tracking-wide sticky top-0 bg-base-100 z-2">
              Preview of items to migrate
            </li>

            {previewData.map((item: any, index: number) => {
              return (
                <li key={index} className="list-row">
                  <div className="flex items-center justify-center w-10 h-10">
                    {item?.item_type ? (
                      getIcon(item.item_type)
                    ) : (
                      <MdHelp className="w-6 h-6 text-info" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">
                      {item?.metabase_name || "Unknown"}
                    </div>
                    <div className="text-xs">
                      {item?.preview_data?.description || "No description"}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <span className="badge badge-primary badge-outline">
                      {item?.preview_data?.visualization_type ||
                        item?.item_type ||
                        "unknown"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-error mt-4">
            <span>{error}</span>
          </div>
        )}

        {/* Execute Button */}
        {!migrationComplete && (
          <button
            type="button"
            className="btn bg-purple-600 text-white hover:bg-purple-500 mx-auto w-full mt-6 max-w-md block"
            disabled={isLoading || previewData.length === 0}
            onClick={handleExecuteMigration}
          >
            {isLoading ? (
              <span className="loading loading-spinner" />
            ) : (
              "Execute Migration"
            )}
          </button>
        )}
      </>
      {migrationComplete && (
        <div className="mt-12">
          {/* Migration Success Screen */}

          <h3 className="text-lg font-bold">
            <span className="flex items-center gap-2">
              <FiCheckCircle className="text-success" />
              Migration Complete!
            </span>
          </h3>

          {migrationSummary && (
            <div className="stats stats-vertical lg:stats-horizontal shadow w-full mb-6">
              <div className="stat">
                <div className="stat-title">Databases</div>
                <div className="stat-value text-primary">
                  {migrationSummary.databases_migrated || 0}
                </div>
              </div>

              <div className="stat">
                <div className="stat-title">Questions</div>
                <div className="stat-value text-info">
                  {migrationSummary.questions_migrated || 0}
                </div>
              </div>

              <div className="stat">
                <div className="stat-title">Dashboards</div>
                <div className="stat-value text-success">
                  {migrationSummary.dashboards_migrated || 0}
                </div>
              </div>

              {migrationSummary.errors &&
                migrationSummary.errors.length > 0 && (
                  <div className="stat">
                    <div className="stat-title">Errors</div>
                    <div className="stat-value text-error">
                      {migrationSummary.errors.length}
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="prose max-w-none">
            <h3>Next Steps:</h3>
            <ol>
              <li>
                <strong>Database Connections:</strong> Go to Data Connections
                and re-enter credentials for migrated databases
              </li>
              <li>
                <strong>Chat Sessions:</strong> Check your chat sessions for
                migrated questions with preserved SQL queries
              </li>
              <li>
                <strong>Analysis Documents:</strong> Review documents created
                from your dashboards
              </li>
            </ol>
          </div>
          <button
            type="button"
            className="btn bg-purple-600 text-white hover:bg-purple-500 mx-auto w-full mt-6 max-w-md block"
            onClick={handleCancel}
          >
            Close & View Connections
          </button>
        </div>
      )}
    </div>
  );
};

export default Migration;
